import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema, tzHeadersSchema } from '../schemas';

// ============================================================
// Query Schemas
// ============================================================

/**
 * `projectId` is gone from every query below: the project is addressed by the
 * URI. A required query param is one `.optional()` away from an all-projects
 * read, whereas the router refuses a missing path segment before any hook runs.
 */

const sequenceItemProgressBySequenceItemsQuerySchema = z.object({
	endDate: requiredStringSchema,
});

const dateRangeQuerySchema = z.object({
	startDate: requiredStringSchema,
	endDate: requiredStringSchema,
});

const milestonesProgressSummaryQuerySchema = z.object({
	date: z.string().optional(),
});

// ============================================================
// Contract
// ============================================================

export const progressContract = defineContract(
	'progress',
	{
		getSequenceItemProgressBySequenceItems: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItemProgressBySequenceItems',
			params: projectIdParamsSchema,
			query: sequenceItemProgressBySequenceItemsQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getSequenceItemProgressBySequenceItems',
			tags: ['Progress'],
			summary: 'Get sequence item progress by sequence items',
		}),
		getSequenceItemPeriodProgressOverall: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItemPeriodProgressOverallReport',
			params: projectIdParamsSchema,
			query: dateRangeQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getSequenceItemPeriodProgressOverall',
			tags: ['Progress'],
			summary: 'Get sequence item period progress overall report',
		}),
		getSequenceItemProgressByLocation: route({
			method: 'GET',
			path: '/projects/:projectId/sequenceItemProgressByLocationReport',
			params: projectIdParamsSchema,
			query: dateRangeQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getSequenceItemProgressByLocation',
			tags: ['Progress'],
			summary: 'Get sequence item progress by location report',
		}),
		getMilestonesProgressSummary: route({
			method: 'GET',
			path: '/projects/:projectId/milestonesProgressSummary',
			params: projectIdParamsSchema,
			query: milestonesProgressSummaryQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getMilestonesProgressSummary',
			tags: ['Progress'],
			summary: 'Get milestones progress summary',
		}),
	},
	{ root: true }
);
